import { IUserPlaylist } from 'contexts/playlists/types';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AvatarLetter } from 'components/avatar';
import Tooltip from 'components/tooltip';

interface ItemUserProps {
  user: IUserPlaylist;
  teamName?: string;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  overflow: hidden;
  padding-right: 10px;
`;

const UserName = styled.p`
  display: block;
  margin: 0;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #1a2535;
`;

const AvatarWrapper = styled.div`
  flex-shrink: 0;
`;

const NameContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const EmailData = styled.div`
  display: flex;
  align-items: baseline;
  overflow: hidden;
  white-space: nowrap;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #57575c;
`;

const EmailText = styled.span`
  flex-shrink: 0;
`;

const TeamNameSpan = styled.span`
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  color: #57575c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  margin-left: 3px;
`;

const Avatar = (props: ItemUserProps): JSX.Element => {
  const { user, teamName } = props;
  const profile = user?.profile?.data;
  const firstName = profile?.first_name?.trim() || user?.first_name?.trim();
  const lastName = profile?.last_name?.trim() || user?.last_name?.trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const displayName = fullName || user?.email || 'Unknown user';
  const showEmail = Boolean(user?.email) && user?.email !== displayName;
  const avatar = profile?.avatar || user?.avatar;

  const teamNameRef = useRef<HTMLSpanElement>(null);
  const [isTeamNameTruncated, setIsTeamNameTruncated] = useState(false);

  useEffect(() => {
    const el = teamNameRef.current;
    if (!el) return;

    const check = () => setIsTeamNameTruncated(el.scrollWidth > el.clientWidth);
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [teamName]);

  return (
    <Container>
      <AvatarWrapper>
        <AvatarLetter avatar={avatar} name={displayName} email={user.email} />
      </AvatarWrapper>
      <NameContainer>
        <UserName>{displayName}</UserName>
        {showEmail && (
          <EmailData>
            <EmailText>{user.email}</EmailText>
            {teamName && (
              <Tooltip title={isTeamNameTruncated ? teamName : undefined} placement="top">
                <TeamNameSpan ref={teamNameRef}> via {teamName}</TeamNameSpan>
              </Tooltip>
            )}
          </EmailData>
        )}
      </NameContainer>
    </Container>
  );
};

export default Avatar;
